import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { PRODUCTS, SERVICES } from "@/data/content";
import { SpotlightCard } from "@/components/SpotlightCard";
import { Seo } from "@/components/Seo";
import { crumbs, productsJsonLd } from "@/config/seo";

export default function Shop() {
  return (
    <div data-testid="shop-page" className="pt-32">
      <Seo
        title="Shop Metal Prints & Prices | MetalMuse Saskatchewan"
        description="Premium sublimation metal prints in Saskatchewan — 8×10 for $35, 6×8 for $25, fridge magnets $10 CAD. Free photo enhancement, ready to hang, same-day delivery available."
        path="/shop"
        keywords="metal prints price, aluminum photo print, 8x10 metal print, fridge magnet print, Saskatchewan metal art"
        jsonLd={[crumbs([{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }]), productsJsonLd()]}
      />
      <section className="container-lux pb-10">
        <SectionHeader eyebrow="Shop" title="Premium prints, honest prices" text="Every order includes free professional photo enhancement, high-definition sublimation printing, and ready-to-hang mounting." />
      </section>

      <section className="container-lux pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <SpotlightCard data-testid={`shop-product-${p.id}`} className="group bg-[#121212] border border-white/5 rounded-2xl overflow-hidden hover:border-[#00E5FF]/40 transition-[border-color,transform] duration-500 hover:-translate-y-1 flex flex-col h-full">
                <div className="overflow-hidden aspect-[4/3]">
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-tech text-xl md:text-2xl text-[#F5F5F7]">{p.name}</h3>
                    <p className="text-[#00E5FF] font-semibold whitespace-nowrap text-lg">${p.price} <span className="text-xs font-normal text-[#A1A1A6]">{p.currency}</span></p>
                  </div>
                  <p className="text-sm text-[#A1A1A6] mt-3 leading-relaxed">{p.description}</p>
                  <ul className="mt-5 flex flex-col gap-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#A1A1A6]">
                        <Check size={14} className="text-[#00E5FF]" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/order?product=${encodeURIComponent(p.name)}`} data-testid={`shop-order-${p.id}`} className="mt-7 inline-flex items-center justify-center gap-2 bg-white text-black font-tech text-[11px] uppercase tracking-[0.2em] font-semibold px-6 py-3.5 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 shadow-[0_0_20px_rgba(0,229,255,0.2)] hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] hover:-translate-y-0.5 mt-auto">
                    Order Your Metal Print <ArrowRight size={15} />
                  </Link>
                </div>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <p className="text-sm text-[#A1A1A6] bg-[#121212] border border-white/5 rounded-2xl px-6 py-4">
            Same-day delivery available <span className="text-[#00E5FF] font-semibold">+$5</span> on qualifying orders. Pickup available for qualifying orders.
          </p>
        </Reveal>
      </section>

      <section className="bg-[#0A0A0A] border-t border-white/5 section-spacing">
        <div className="container-lux">
          <SectionHeader eyebrow="What We Print" title="If it matters to you, we'll print it" text="Beyond our standard products, we take on all kinds of custom work:" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s} delay={i * 0.05}>
                <div data-testid={`service-${s.toLowerCase().replace(/\s+/g, "-")}`} className="bg-[#121212] border border-white/5 rounded-2xl px-5 py-7 text-center hover:border-[#00E5FF]/30 transition-colors duration-500">
                  <p className="font-medium text-[#F5F5F7] text-sm md:text-base">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 text-center">
            <Link to="/order" data-testid="shop-bottom-cta" className="inline-flex items-center gap-2 bg-white text-black font-tech text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 rounded-full hover:bg-cyan-200 transition-[background-color,box-shadow,transform] duration-300 shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:-translate-y-1">
              Order Your Metal Print <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
